/**
 * Shared Breadcrumbs Component
 * Provides consistent breadcrumb navigation across all portals
 */

import React from 'react';
import {
  Breadcrumbs as MUIBreadcrumbs,
  Link,
  Typography,
  Box,
  useTheme,
  alpha,
} from '@mui/material';
import { NavigateNext as NavigateNextIcon, Home as HomeIcon } from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useLayout } from '../LayoutProvider';

/**
 * Breadcrumbs Component
 * @param {Object} props - Component props
 * @param {Array} props.items - Custom breadcrumb items (overrides layout breadcrumbs)
 * @param {boolean} props.showHome - Whether to show home breadcrumb
 * @param {string} props.homeLabel - Label for home breadcrumb
 * @param {string} props.homePath - Path for home breadcrumb
 * @param {number} props.maxItems - Maximum number of breadcrumb items to show
 * @param {Function} props.onItemClick - Custom click handler for breadcrumb items
 */
export const Breadcrumbs = ({
  items: customItems,
  showHome = true,
  homeLabel = 'Home',
  homePath = '/',
  maxItems = 8,
  onItemClick,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { breadcrumbs: layoutBreadcrumbs, portalType } = useLayout();

  // Use custom items if provided, otherwise use layout breadcrumbs
  const breadcrumbItems = customItems || layoutBreadcrumbs || [];

  // Handle breadcrumb click
  const handleClick = (item, index) => {
    if (onItemClick) {
      onItemClick(item, index);
    } else if (item.path && item.path !== '#') {
      navigate(item.path);
    }
  };

  // Create home breadcrumb
  const homeBreadcrumb = {
    label: homeLabel,
    path: getPortalHomePath(portalType) || homePath,
    icon: <HomeIcon sx={{ fontSize: 16 }} />,
  };

  // Combine home and other breadcrumbs
  const allBreadcrumbs = showHome ? [homeBreadcrumb, ...breadcrumbItems] : breadcrumbItems;

  // Don't render if no breadcrumbs or only home
  if (allBreadcrumbs.length <= 1 && showHome) {
    return null;
  }

  return (
    <Box
      sx={{
        py: 1,
        px: 2,
        backgroundColor: alpha(theme.palette.background.paper, 0.5),
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        backdropFilter: 'blur(5px)',
      }}
    >
      <MUIBreadcrumbs
        aria-label="breadcrumb"
        separator={<NavigateNextIcon fontSize="small" />}
        maxItems={maxItems}
        sx={{
          '& .MuiBreadcrumbs-ol': {
            alignItems: 'center',
          },
        }}
      >
        {allBreadcrumbs.map((item, index) => {
          const isLast = index === allBreadcrumbs.length - 1;
          const isClickable = item.path && item.path !== '#' && !isLast;

          if (isLast) {
            // Last item - not clickable
            return (
              <Typography
                key={index}
                color="text.primary"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  fontWeight: 500,
                }}
              >
                {item.icon && (
                  <Box sx={{ mr: 0.5, display: 'flex', alignItems: 'center' }}>{item.icon}</Box>
                )}
                {item.label}
              </Typography>
            );
          }

          if (isClickable) {
            // Clickable breadcrumb
            return (
              <Link
                key={index}
                component={RouterLink}
                to={item.path}
                onClick={(e) => {
                  e.preventDefault();
                  handleClick(item, index);
                }}
                underline="hover"
                color="inherit"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  '&:hover': {
                    color: theme.palette.primary.main,
                  },
                }}
              >
                {item.icon && (
                  <Box sx={{ mr: 0.5, display: 'flex', alignItems: 'center' }}>{item.icon}</Box>
                )}
                {item.label}
              </Link>
            );
          }

          // Non-clickable breadcrumb
          return (
            <Typography
              key={index}
              color="text.secondary"
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {item.icon && (
                <Box sx={{ mr: 0.5, display: 'flex', alignItems: 'center' }}>{item.icon}</Box>
              )}
              {item.label}
            </Typography>
          );
        })}
      </MUIBreadcrumbs>
    </Box>
  );
};

/**
 * Get default home path for portal
 * @param {string} portalType - Portal type
 * @returns {string} Home path
 */
const getPortalHomePath = (portalType) => {
  const homePaths = {
    bureau: '/bureau',
    admin: '/admin',
    client: '/client',
    'super-admin': '/super-admin',
  };

  return homePaths[portalType] || '/';
};

/**
 * Hook to manage breadcrumbs
 * @returns {Object} Breadcrumb utilities
 */
export const useBreadcrumbNavigation = () => {
  const { updateBreadcrumbs, breadcrumbs } = useLayout();

  const setBreadcrumbs = (items) => {
    updateBreadcrumbs(items);
  };

  const addBreadcrumb = (item) => {
    updateBreadcrumbs([...breadcrumbs, item]);
  };

  const clearBreadcrumbs = () => {
    updateBreadcrumbs([]);
  };

  const generateFromPath = (pathname, routeConfig = {}) => {
    const segments = pathname.split('/').filter(Boolean);
    const breadcrumbs = [];

    let currentPath = '';
    segments.forEach((segment, index) => {
      currentPath += `/${segment}`;

      // Look up label in route config or use segment
      const label = routeConfig[currentPath] || segment.charAt(0).toUpperCase() + segment.slice(1);

      breadcrumbs.push({
        label,
        path: currentPath,
        key: `breadcrumb-${index}`,
      });
    });

    return breadcrumbs;
  };

  return {
    setBreadcrumbs,
    addBreadcrumb,
    clearBreadcrumbs,
    generateFromPath,
  };
};

export default Breadcrumbs;
